import { useQuery } from "@tanstack/react-query";
import { Button, Tag } from "antd";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ReportEditButton from "../../components/Button/ReportEditButton";
import ReportingExportButton from "../../components/Button/ReportingExportButton";
import AuditLogModal from "../../components/Modal/AuditLogModal";
import { LessonLearnedPDFTemplate } from "../../components/PDF/Reporting/lesson-learned/LessonLearnedPDFTemplate";
import QueryRetry from "../../components/QueryRetry";
import { ReportItemAttachments } from "../../components/Reporting/items/ReportItemParts";
import ReportAssetCard from "../../components/Reporting/ReportAssetCard";
import ReportCreatedLine from "../../components/Reporting/ReportCreatedLine";
import {
	getReviewerFact,
	getTenantFact,
	LookedUpValue,
} from "../../components/Reporting/reportFacts";
import ReportHeader, {
	ReportHeaderFact,
	ReportSummary,
} from "../../components/Reporting/ReportHeader";
import { useGlobalLoading } from "../../hooks/useGlobalLoading";
import { useLookup } from "../../hooks/useLookup";
import useReportDocumentOpener from "../../hooks/useReportDocumentOpener";
import useReportDocumentsDrawer from "../../hooks/useReportDocumentsDrawer";
import useReportEditor from "../../hooks/useReportEditor";
import { getIncidentReportList } from "../../services/api-server/incident_report";
import { getLessonLearnedList } from "../../services/api-server/lesson_learned";
import { useTenantStore } from "../../stores/tenantStore";
import { useUserStore } from "../../stores/userStore";
import { GetAntIcon } from "../../utils/ant_icons";
import {
	canOpenIncidentPage,
	getLessonFieldText,
	getLinkedIncidentId,
	getShownLinkedIncident,
	ShownIncident,
} from "../../utils/reporting/lessonLearned";
import { createLessonLearnedLookupConfig } from "../../utils/reporting/lookupConfigs/lesson-learned";
import { getReportAuthorEmail } from "../../utils/reporting/reportAuthor";
import { getShownReportDocuments } from "../../utils/reporting/reportFiles";
import { getReportStatusDisplay } from "../../utils/reporting/statusDisplay";
import { handleExportReport } from "../../utils/reports";
import { checkSuperAdminPriviledge } from "../../utils/utils";

// What was done is added at final review, by the person the reviewer assigns.
const WHAT_WAS_DONE_PENDING =
	"Not added yet. The person the reviewer assigned adds it during final review.";

// The incident a lesson is related to, as its page names it: only for a
// lesson in the viewer's tenants and for people who could open the incident's
// page, and only when the incident belongs to one of their tenants.
function useRelatedIncident(
	lesson: any,
	isOwnTenant: boolean,
	tenantKeys: string[]
): ShownIncident | undefined {
	const tenant = useTenantStore((state) => state.tenant);
	const user = useUserStore((state) => state.user);
	const userRole = useUserStore((state) => state.userRole);

	const incidentId = getLinkedIncidentId(lesson);
	const isShown =
		isOwnTenant &&
		canOpenIncidentPage(
			tenant,
			user?.email,
			checkSuperAdminPriviledge(userRole)
		);
	const { data: incident } = useQuery({
		queryKey: ["lesson-linked-incident", incidentId],
		queryFn: async () => {
			const response: any = await getIncidentReportList({ _id: incidentId });
			return response?.[0] ?? null;
		},
		enabled: !!incidentId && isShown,
		staleTime: 5 * 60 * 1000,
	});
	// A query that can't run any more still holds what it fetched.
	return isShown ? getShownLinkedIncident(incident, tenantKeys) : undefined;
}

// The line under the header's created line: the incident, linked to its page.
function LessonRelatedIncident({
	incident,
}: Readonly<{ incident: ShownIncident }>) {
	return (
		<>
			<span aria-hidden="true">{GetAntIcon("link")}</span>
			<span>Related to incident</span>
			<Link to={`/reporting/incident-stream/${incident.id}`}>
				{incident.number}
			</Link>
			{incident.title ? <span>{incident.title}</span> : null}
		</>
	);
}

// A lesson's text sections, in columns, then its attachments. Without
// onOpenDocument the attachments' buttons are disabled.
function LessonTextSections({
	lesson,
	onOpenDocument,
}: Readonly<{
	lesson: any;
	onOpenDocument?: (doc: any, documents: any[]) => void;
}>) {
	const isOpen =
		typeof lesson?.status === "string" &&
		lesson.status.toLowerCase() === "open";
	const attachments = getShownReportDocuments(lesson?.file_upload);

	return (
		<>
			<div className="report-header__summary-grid">
				<ReportSummary label="What was planned" text={lesson?.what_planned} />
				<ReportSummary
					label={
						<>
							<span className="report-header__label-icon" aria-hidden="true">
								{GetAntIcon("bulb")}
							</span>
							<span>What was learned</span>
						</>
					}
					text={lesson?.what_learned}
				/>
				<ReportSummary
					label="What actions are required"
					text={lesson?.what_actions_required}
				/>
				<ReportSummary
					label="What was done"
					text={lesson?.what_was_done}
					emptyText={isOpen ? WHAT_WAS_DONE_PENDING : undefined}
				/>
			</div>
			{attachments.length > 0 && (
				<div className="report-header__summary">
					<span className="report-header__label">Attachments</span>
					<ReportItemAttachments
						documents={attachments}
						onOpenDocument={onOpenDocument}
					/>
				</div>
			)}
		</>
	);
}

function LessonLearnedOverview({ reportId }: Readonly<{ reportId: string }>) {
	const [auditLogOpen, setAuditLogOpen] = useState(false);

	const { data, isLoading, isError } = useQuery({
		queryKey: ["lesson-learned-overview", reportId],
		queryFn: async () => {
			const response: any = await getLessonLearnedList({ _id: reportId });
			return response[0] ?? null;
		},
		enabled: !!reportId,
	});

	const editor = useReportEditor("lesson_learned", data, [
		"lesson-learned-overview",
		reportId,
	]);
	const documents = useReportDocumentsDrawer("lesson_learned", reportId, data);
	const { openDocument, preview } = useReportDocumentOpener(
		data?.operator ?? "",
		data?.folder_id
	);

	useGlobalLoading("lesson-learned-overview", isLoading);

	const tenantList = useTenantStore((state) => state.tenantList);

	// A global lesson can come from a tenant the viewer isn't in. Its project,
	// asset and other tenant data can't be looked up then: only the shared
	// lists, such as its origin and lesson type, can.
	const tenantKeys: string[] = tenantList.map((item: any) => item?.role_key);
	const operator: string | undefined = data?.operator;
	const isOwnTenant = !!operator && tenantKeys.includes(operator);

	const lookupConfig = useMemo(
		() =>
			createLessonLearnedLookupConfig(
				operator && isOwnTenant ? [operator] : []
			),
		[operator, isOwnTenant]
	);

	// Keyed by its scope too: the lookup's own key only holds ids, so a tenant
	// list that arrives late still brings the full lookup.
	const { dataWithLookup, isLoading: isLoadingLookup } = useLookup(
		[
			"lookup",
			"lesson-learned-overview",
			reportId,
			isOwnTenant ? "own" : "shared",
		],
		data ? [data] : [],
		lookupConfig,
		0
	);
	const lookupData = dataWithLookup[0];

	const relatedIncident = useRelatedIncident(data, isOwnTenant, tenantKeys);

	// Another tenant's documents can't be listed: their folder is reached
	// through that tenant's container, which the viewer's tenants don't have.
	const documentsDrawer = isOwnTenant ? documents.drawer : null;

	if (isError) {
		// The drawers and the preview stay, so a failed background refetch
		// doesn't close them.
		return (
			<>
				<QueryRetry queryKey={["lesson-learned-overview", reportId]} />
				{preview}
				{documentsDrawer}
				{editor.drawer}
			</>
		);
	}

	const isRejected = data?.is_rejected === true;
	const statusDisplay = getReportStatusDisplay(
		data?.status,
		data?.open_status,
		isRejected
	);

	// A fact the lookup names: a placeholder while it loads, then the name, or
	// a dash when there is none.
	const namedFact = (
		key: string,
		label: string,
		value?: string
	): ReportHeaderFact => ({
		key,
		label,
		value: <LookedUpValue value={value} isLoading={isLoadingLookup} />,
	});

	const assetName = getLessonFieldText(lookupData?.well_name, data?.well);
	const projectArea = getLessonFieldText(lookupData?.project_area);
	const asset = assetName ? (
		<>
			{projectArea ? <Tag>{projectArea}</Tag> : null}
			{assetName}
		</>
	) : undefined;

	const facts: ReportHeaderFact[] = [
		namedFact(
			"project",
			"Project",
			getLessonFieldText(lookupData?.project_name, data?.project)
		),
		{
			key: "asset",
			label: "Asset",
			value: <LookedUpValue value={asset} isLoading={isLoadingLookup} />,
		},
		namedFact(
			"operation",
			"Operation",
			getLessonFieldText(lookupData?.operation_name, data?.operation)
		),
		namedFact(
			"phase",
			"Phase",
			getLessonFieldText(lookupData?.phase_name, data?.phase)
		),
		namedFact(
			"discipline",
			"Discipline",
			getLessonFieldText(lookupData?.discipline_name, data?.discipline)
		),
		namedFact("origin", "Origin", getLessonFieldText(lookupData?.origin)),
		// The form asks for a section on Wells projects only.
		...(data?.section
			? [
					namedFact(
						"section",
						"Section",
						getLessonFieldText(lookupData?.section)
					),
				]
			: []),
		namedFact(
			"lesson_type",
			"Lesson type",
			getLessonFieldText(lookupData?.lesson_learned_type)
		),
		namedFact(
			"lesson_classification",
			"Lesson classification",
			getLessonFieldText(lookupData?.lesson_learned_classification)
		),
		getReviewerFact(lookupData, isLoadingLookup),
		getTenantFact(data?.operator),
	];

	// A lesson imported with legacy data only has the date it was created.
	const submittedOn = data?.legacy_data ? data?.created_on : data?.submitted_on;

	const created = (
		<ReportCreatedLine
			submittedOn={submittedOn}
			authorName={lookupData?.author_name}
			authorEmail={getReportAuthorEmail(data)}
			historyTitle="Lesson learned review history"
			historyData={lookupData ?? data}
			onOpenAuditLog={() => setAuditLogOpen(true)}
		/>
	);

	return (
		<>
			{data && (
				// Keyed by report: another lesson opened in place starts expanded,
				// with its text clamped.
				<ReportHeader
					key={reportId}
					label="Lesson learned details"
					reportNumber={data.report_number}
					isInternal={Boolean(data.internal_report)}
					title={data.title}
					status={statusDisplay}
					actions={
						<>
							{editor.canEdit && (
								<ReportEditButton
									type="default"
									label={editor.label}
									onClick={editor.open}
								/>
							)}
							{isOwnTenant && (
								<Button icon={GetAntIcon("file2")} onClick={documents.open}>
									Additional documents
								</Button>
							)}
							<ReportingExportButton
								type="default"
								onExport={() =>
									handleExportReport(
										lookupData,
										"lesson_learned",
										LessonLearnedPDFTemplate
									)
								}
								isLoading={isLoadingLookup}
							/>
						</>
					}
					created={created}
					related={
						relatedIncident && (
							<LessonRelatedIncident incident={relatedIncident} />
						)
					}
					facts={facts}
					media={
						<ReportAssetCard
							rigName={getLessonFieldText(lookupData?.rig)}
							rigImage={lookupData?.rig_image}
							wellName={assetName}
							// Not for another tenant's lesson: the card would fetch
							// its well through that tenant.
							wellId={isOwnTenant ? data.well : undefined}
							operatorKey={data.operator}
							isLoading={isLoadingLookup}
						/>
					}
					summaries={[
						{
							key: "what_happened",
							label: "What happened",
							text: data.what_happened,
						},
					]}
					sections={
						// Files are fetched through the lesson's tenant, which a
						// viewer from another tenant can't use: their buttons are
						// disabled.
						<LessonTextSections
							lesson={data}
							onOpenDocument={isOwnTenant ? openDocument : undefined}
						/>
					}
				/>
			)}
			{/* Outside the header, so collapsing it never closes the log. */}
			{data && (
				<AuditLogModal
					auditlogFilter={{ level: "lesson_learned", level_id: data._id }}
					label="Lesson learned"
					type="lesson_learned"
					open={auditLogOpen}
					handleClose={() => {
						setAuditLogOpen(false);
					}}
				/>
			)}
			{preview}
			{documentsDrawer}
			{editor.drawer}
		</>
	);
}

export default LessonLearnedOverview;
